import React, { useRef, useEffect, useState, useCallback } from 'react';
import './SearchBox.css';

const EXAMPLE_PROMPTS = [
  "Who are the top 5 people I should follow up with?",
  "Tell me more about John Smith",
  "I just met someone named Bruce Wayne who might give $100,000",
  "What meetings do I have coming up?",
  "Add a note: Sarah mentioned she's interested in our youth program"
];

function SearchBox({ placeholder, value, onChange, onSearch, expandable = false, donors = [], leftAction = null, loading = false, autoFocus = false, showExamples = false, noHoverEffect = false, rainbowPlaceholder = false, exampleLine = null }) {
  const textareaRef = useRef(null);
  const inputRef = useRef(null);
  const measureRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const hasExpandedOnce = useRef(false);
  const [suggestion, setSuggestion] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  // Cycle through examples when showExamples is true
  useEffect(() => {
    if (!showExamples || isFocused || value) return;

    const interval = setInterval(() => {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setCurrentExampleIndex(prev => (prev + 1) % EXAMPLE_PROMPTS.length);
        setIsAnimatingOut(false);
      }, 200);
    }, 3500);

    return () => clearInterval(interval);
  }, [showExamples, isFocused, value]);

  // Resize textarea to fit example prompt
  const exampleMeasureRef = useRef(null);
  useEffect(() => {
    if (!showExamples || isFocused || value || !textareaRef.current || !exampleMeasureRef.current) return;

    // Measure the example text height
    const measure = exampleMeasureRef.current;
    measure.textContent = EXAMPLE_PROMPTS[currentExampleIndex];

    // Set textarea min-height to match example
    const height = measure.offsetHeight;
    if (height > 0) {
      textareaRef.current.style.height = height + 'px';
    }
  }, [showExamples, currentExampleIndex, isFocused, value]);

  const handleFocus = () => {
    setIsFocused(true);
    if (value) {
      onChange('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Find matching donor name as user types
  useEffect(() => {
    if (!value || value.length < 2 || donors.length === 0) {
      setSuggestion(null);
      return;
    }

    const lowerValue = value.toLowerCase();
    const match = donors.find(name =>
      name.toLowerCase().startsWith(lowerValue) &&
      name.toLowerCase() !== lowerValue
    );

    setSuggestion(match || null);
  }, [value, donors]);

  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && suggestion) {
      e.preventDefault();
      onChange(suggestion);
      setSuggestion(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSearch();
    }
  };

  // Measure text width and handle expansion + height resize together
  const checkOverflowAndResize = useCallback(() => {
    if (!expandable || !textareaRef.current || !measureRef.current) return;

    const textarea = textareaRef.current;
    const measure = measureRef.current;
    const containerWidth = textarea.offsetWidth - 80; // Account for button

    // Measure the text at full size
    measure.style.fontSize = '2.5rem';
    measure.textContent = value || '';
    const textWidthAtFullSize = measure.offsetWidth;

    // Check if we need to expand (shrink text)
    const shouldExpand = textWidthAtFullSize > containerWidth;

    if (shouldExpand && !hasExpandedOnce.current) {
      hasExpandedOnce.current = true;
      setIsExpanded(true);
    }

    // Only reset if text is very short
    if (value.length < 15) {
      hasExpandedOnce.current = false;
      setIsExpanded(false);
    }

    // Resize height with smooth transition
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const currentHeight = textarea.offsetHeight;

      // Temporarily remove height to measure natural scrollHeight
      // Use minHeight trick to avoid visual collapse
      const prevHeight = textarea.style.height;
      textarea.style.minHeight = currentHeight + 'px';
      textarea.style.height = 'auto';
      const newHeight = textarea.scrollHeight;
      textarea.style.height = prevHeight;
      textarea.style.minHeight = '';

      // Only animate if height is actually changing
      if (currentHeight !== newHeight) {
        textarea.style.height = currentHeight + 'px';
        // Force reflow to enable transition
        void textarea.offsetHeight;
        textarea.style.height = newHeight + 'px';
      }
    }
  }, [value, expandable]);

  useEffect(() => {
    checkOverflowAndResize();
  }, [checkOverflowAndResize]);

  // When isExpanded changes, recalculate height after font transition
  useEffect(() => {
    if (!expandable || !textareaRef.current) return;

    // Wait for the font-size transition to complete (300ms in CSS)
    const timer = setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
      }
    }, 50); // Small delay to let the CSS transition start

    return () => clearTimeout(timer);
  }, [isExpanded, expandable]);

  // Auto-focus on mount if requested
  useEffect(() => {
    if (autoFocus) {
      const ref = expandable ? textareaRef : inputRef;
      if (ref.current) {
        ref.current.focus();
      }
    }
  }, [autoFocus, expandable]);

  // Capture any keystrokes to focus input (but don't auto-focus on mount)
  useEffect(() => {
    // Don't capture global keys if this is an autoFocus searchbox (inside a modal)
    if (autoFocus) return;

    const ref = expandable ? textareaRef : inputRef;

    // Listen for any keystrokes and focus the input
    const handleGlobalKeydown = (e) => {
      // Ignore if a modal/panel overlay is open and this SearchBox is NOT inside it
      const overlay = document.querySelector('.donor-panel-overlay, .modal-overlay');
      if (overlay && !overlay.contains(ref.current)) {
        return;
      }

      // Ignore if already focused on an input, or if modifier keys are pressed
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA' ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey
      ) {
        return;
      }

      // Focus the input for printable characters
      if (e.key.length === 1 && ref.current) {
        ref.current.focus();
      }
    };

    window.addEventListener('keydown', handleGlobalKeydown);
    return () => window.removeEventListener('keydown', handleGlobalKeydown);
  }, [expandable, autoFocus]);

  if (expandable) {
    return (
      <div className={`search-box ${isExpanded ? 'expanded' : ''} ${leftAction ? 'has-actions' : ''} ${loading ? 'loading' : ''} ${noHoverEffect ? 'no-hover' : ''}`}>
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyPress={handleKeyPress}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`search-input search-textarea ${isExpanded ? 'text-smaller' : ''}`}
            rows={1}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-form-type="other"
          />
          {!value && !isFocused && (
            showExamples ? (
              <span className={`animated-placeholder example-prompt ${isAnimatingOut ? 'animating-out' : 'animating-in'}`}>
                {EXAMPLE_PROMPTS[currentExampleIndex]}
                <span className="blinking-cursor rainbow-cursor"></span>
              </span>
            ) : placeholder ? (
              <span className={`animated-placeholder ${rainbowPlaceholder ? 'rainbow' : ''}`}>
                {placeholder}...
                {exampleLine && (
                  <>
                    <br />
                    <span className="placeholder-example">{exampleLine}</span>
                  </>
                )}
                <span className="blinking-cursor rainbow-cursor"></span>
              </span>
            ) : null
          )}
          {suggestion && (
            <span className={`autocomplete-suggestion ${isExpanded ? 'text-smaller' : ''}`}>
              <span className="autocomplete-typed">{value}</span>
              <span className="autocomplete-completion">{suggestion.slice(value.length)}</span>
            </span>
          )}
          {/* Hidden element for measuring text width */}
          <span
            ref={measureRef}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              fontWeight: 400,
              letterSpacing: '-0.02em'
            }}
          />
          {/* Hidden element for measuring example prompt height */}
          {showExamples && (
            <span
              ref={exampleMeasureRef}
              style={{
                position: 'absolute',
                visibility: 'hidden',
                width: 'calc(100% - 60px)',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                fontSize: '1.5rem',
                fontWeight: 400,
                letterSpacing: '-0.02em',
                lineHeight: 1.3
              }}
            />
          )}
        </div>
        {leftAction ? (
          <div className="search-actions-row">
            <div className="left-action">{leftAction}</div>
            <button onClick={onSearch} className="search-button">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ transform: 'translateX(1px)' }}>
                <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        ) : (
          <button onClick={onSearch} className="search-button">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ transform: 'translateX(1px)' }}>
              <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`search-box ${loading ? 'loading' : ''}`}>
      <div className="input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="search-input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-form-type="other"
        />
        {!value && !isFocused && placeholder && <span className={`animated-placeholder ${rainbowPlaceholder ? 'rainbow' : ''}`}>{placeholder}...<span className="blinking-cursor rainbow-cursor"></span></span>}
        {suggestion && (
          <span className="autocomplete-suggestion">
            <span className="autocomplete-typed">{value}</span>
            <span className="autocomplete-completion">{suggestion.slice(value.length)}</span>
          </span>
        )}
      </div>
      <button onClick={onSearch} className="search-button">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ transform: 'translateX(1px)' }}>
          <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}

export default SearchBox;
